/**
 * Dynamic content translations for data coming from the backend
 * This provides Google Translate-like functionality for Arabic <-> English
 * Automatically translates any text based on the current language setting
 */

// =============================================================================
// COMPREHENSIVE TRANSLATION DICTIONARY (Arabic -> English)
// =============================================================================

// Organization names
const organizationTranslations: Record<string, string> = {
  'مجلس محمد بن حمد الشرقي': 'Mohammed bin Hamad Al Sharqi Council',
  'بلدية الفجيرة': 'Fujairah Municipality',
  'دائرة الموارد البشرية': 'Human Resources Department',
  'دائرة المالية': 'Finance Department',
  'دائرة الأراضي والتخطيط العمراني': 'Land and Urban Planning Department',
  'دائرة الصحة': 'Health Department',
  'دائرة التعليم': 'Education Department',
  'دائرة الاقتصاد والسياحة': 'Economy and Tourism Department',
  'هيئة الفجيرة للبيئة': 'Fujairah Environment Authority',
  'هيئة الطرق والمواصلات': 'Roads and Transport Authority',
  'المجلس التنفيذي': 'Executive Council',
  'الديوان': 'Diwan',
  'دائرة السياحة والآثار': 'Fujairah Tourism & Antiquities Authority',
  'هيئة الفجيرة للثقافة والإعلام': 'Fujairah Culture and Media Authority',
  'نادي الفجيرة الرياضي الثقافي': 'Fujairah Sports and Cultural Club',
  'المجالس المفتوحة': 'Open Councils',
  'أوركسترا الفجيرة': 'Fujairah Orchestra',
  'الفجيرة للعطلات': 'Fujairah Holidays',
  'جامعة الفجيرة': 'Fujairah University',
  'شؤون المقابر': 'Cemetery Affairs',
  'مبادرة البدر': 'Al Badr Initiative',
  'مدرسة الخط والزخرفة': 'Calligraphy and Ornamentation School',
  'مركز الفجيرة للمغامرات': 'Fujairah Adventure Center',
  'أكاديمية الفجيرة للفنون الجميلة': 'Fujairah Fine Arts Academy',
  'مؤسسة الفجيرة لتنمية المناطق': 'Fujairah Regional Development Foundation',
  'نادي الفجيرة للفنون القتالية': 'Fujairah Martial Arts Club',
  'مؤسسة أداء للتطوير المهني المؤسسي': 'Adaa Foundation for Institutional Professional Development',
};

// Unit types
const unitTypeTranslations: Record<string, string> = {
  'إدارة': 'Department',
  'قسم': 'Section',
  'شعبة': 'Division',
  'مكتب': 'Office',
  'وحدة': 'Unit',
  'فرع': 'Branch',
  'مركز': 'Center',
  'هيئة': 'Authority',
  'مؤسسة': 'Foundation',
  'دائرة': 'Department',
};

// Status labels
const statusTranslations: Record<string, string> = {
  'معتمد': 'Approved',
  'مرفوض': 'Rejected',
  'قيد المراجعة': 'Under Review',
  'مسودة': 'Draft',
  'مكتمل': 'Completed',
  'جاري التنفيذ': 'In Progress',
  'متأخر': 'Delayed',
  'ملغي': 'Cancelled',
  'معلق': 'Pending',
  'نشط': 'Active',
  'غير نشط': 'Inactive',
  'منخفض': 'Low',
  'متوسط': 'Medium',
  'مرتفع': 'High',
  'حرج': 'Critical',
  'ممتاز': 'Excellent',
  'جيد': 'Good',
  'مقبول': 'Acceptable',
  'ضعيف': 'Poor',
  'على المسار': 'On Track',
  'متقدم': 'Ahead',
  'متراجع': 'Behind',
};

// Common words and phrases
const commonTranslations: Record<string, string> = {
  // General
  'الرئيسية': 'Home',
  'لوحة المعلومات': 'Dashboard',
  'الإعدادات': 'Settings',
  'تسجيل الخروج': 'Logout',
  'تسجيل الدخول': 'Login',
  'حفظ': 'Save',
  'إلغاء': 'Cancel',
  'حذف': 'Delete',
  'تعديل': 'Edit',
  'إضافة': 'Add',
  'بحث': 'Search',
  'تصفية': 'Filter',
  'تصدير': 'Export',
  'استيراد': 'Import',
  'عرض التفاصيل': 'View Details',
  'عرض الكل': 'View All',
  'إغلاق': 'Close',
  'تأكيد': 'Confirm',
  'نعم': 'Yes',
  'لا': 'No',
  'موافق': 'OK',
  'رجوع': 'Back',
  'التالي': 'Next',
  'السابق': 'Previous',

  // Strategy & Goals
  'الأهداف الاستراتيجية': 'Strategic Goals',
  'الأهداف': 'Goals',
  'هدف استراتيجي': 'Strategic Goal',
  'المبادرات': 'Initiatives',
  'مبادرة': 'Initiative',
  'الأنشطة': 'Activities',
  'نشاط': 'Activity',
  'مؤشرات الأداء': 'KPIs',
  'مؤشر أداء': 'KPI',
  'العمليات': 'Processes',
  'عملية': 'Process',
  'الخدمات': 'Services',
  'خدمة': 'Service',
  'أهداف الوحدات': 'Unit Goals',
  'هدف الوحدة': 'Unit Goal',
  'المخاطر': 'Risks',
  'خطر': 'Risk',

  // Performance
  'نسبة الإنجاز': 'Achievement Rate',
  'نسبة التحقيق': 'Achievement Percentage',
  'الأداء': 'Performance',
  'أداء الجهات': 'Organizations Performance',
  'مؤشر انحراف الأداء': 'Performance Deviation Index',
  'مؤشر تحقيق الأهداف الاستراتيجية': 'Strategic Goals Achievement Index',
  'القيمة المستهدفة': 'Target Value',
  'القيمة الفعلية': 'Actual Value',
  'القيمة المحققة': 'Achieved Value',
  'الوزن': 'Weight',
  'الاستراتيجية': 'Strategy',
  'رأس المال البشري': 'Human Capital',

  // Time periods
  'يومي': 'Daily',
  'أسبوعي': 'Weekly',
  'شهري': 'Monthly',
  'ربع سنوي': 'Quarterly',
  'نصف سنوي': 'Semi-Annual',
  'سنوي': 'Annual',

  // Risk types
  'خطر استراتيجي': 'Strategic Risk',
  'خطر تشغيلي': 'Operational Risk',
  'خطر مالي': 'Financial Risk',
  'خطر امتثال': 'Compliance Risk',

  // KPI types
  'استراتيجي': 'Strategic',
  'تشغيلي': 'Operational',
  'كفاءة': 'Efficiency',
  'فعالية': 'Effectiveness',
  'أثر': 'Impact',
  'إيجابي': 'Positive',
  'سلبي': 'Negative',

  // Organization structure
  'الوحدات التنظيمية': 'Organizational Units',
  'الوحدة التنظيمية': 'Organizational Unit',
  'الموظفين': 'Employees',
  'موظف': 'Employee',
  'المدير': 'Manager',
  'المسؤول': 'Responsible',
  'الجهة': 'Organization',
  'الجهات': 'Organizations',
  'جهتي': 'My Organization',

  // Comments & Notes
  'الملاحظات': 'Notes',
  'ملاحظة': 'Note',
  'التعليقات': 'Comments',
  'تعليق': 'Comment',
  'رد': 'Reply',
  'استفسار': 'Inquiry',
  'تم التعديل': 'Edited',

  // Actions
  'طلب حذف': 'Request Delete',
  'موافقة': 'Approve',
  'رفض': 'Reject',
  'إرسال': 'Submit',
  'مراجعة': 'Review',
  'اعتماد': 'Approve',

  // Dates
  'تاريخ البداية': 'Start Date',
  'تاريخ النهاية': 'End Date',
  'تاريخ الإنشاء': 'Created Date',
  'تاريخ التحديث': 'Updated Date',
  'تاريخ الاستحقاق': 'Due Date',

  // Measurement units
  'درهم': 'AED',
  'يوم': 'Day',
  'ساعة': 'Hour',
  'عدد': 'Number',

  // Counts
  'إجمالي': 'Total',
  'المجموع': 'Sum',
  'المتوسط': 'Average',

  // Messages
  'لا توجد بيانات': 'No data available',
  'جاري التحميل': 'Loading...',
  'تم الحفظ بنجاح': 'Saved successfully',
  'حدث خطأ': 'An error occurred',
  'هل أنت متأكد؟': 'Are you sure?',
  'هل أنت متأكد من الحذف؟': 'Are you sure you want to delete?',

  // User types
  'فريق الاستراتيجية': 'Strategy Team',
  'موظف القسم': 'Department Employee',
  'مدير القسم': 'Department Director',
  'مدير الجهة': 'Authority Director',
  'موظف الجهة': 'Department Employee',
  'متخذ القرار': 'Authority Director',
  'مدير النظام': 'System Administrator',

  // Reports
  'التقارير': 'Reports',
  'تقرير': 'Report',
  'تصدير PDF': 'Export PDF',
  'تصدير Excel': 'Export Excel',
  'طباعة': 'Print',

  // Help
  'المساعدة': 'Help',
  'الدعم الفني': 'Technical Support',
  'دليل المستخدم': 'User Guide',
  'الأسئلة الشائعة': 'FAQ',

  // Navigation
  'القائمة الرئيسية': 'Main Menu',
  'العودة للرئيسية': 'Back to Home',
  'الصفحة التالية': 'Next Page',
  'الصفحة السابقة': 'Previous Page',
};

// Goal-related phrases
const goalTranslations: Record<string, string> = {
  'تحقيق التميز المؤسسي': 'Achieving Institutional Excellence',
  'تطوير الموارد البشرية': 'Human Resources Development',
  'تحسين جودة الخدمات': 'Improving Service Quality',
  'تعزيز الابتكار': 'Promoting Innovation',
  'رفع كفاءة العمليات': 'Improving Process Efficiency',
  'تحقيق رضا المتعاملين': 'Achieving Customer Satisfaction',
  'تطوير البنية التحتية': 'Infrastructure Development',
  'تعزيز الشراكات': 'Strengthening Partnerships',
  'تحقيق الاستدامة': 'Achieving Sustainability',
  'تطوير القدرات': 'Capacity Building',
};

// Process-related phrases
const processTranslations: Record<string, string> = {
  'معالجة الطلبات': 'Request Processing',
  'إدارة الشكاوى': 'Complaint Management',
  'متابعة المشاريع': 'Project Follow-up',
  'إعداد التقارير': 'Report Preparation',
  'مراجعة الأداء': 'Performance Review',
  'التخطيط الاستراتيجي': 'Strategic Planning',
  'إدارة الموارد': 'Resource Management',
  'ضمان الجودة': 'Quality Assurance',
  'التدقيق الداخلي': 'Internal Audit',
  'إدارة المخاطر': 'Risk Management',
};

// Period labels (schedule period names)
const periodTranslations: Record<string, string> = {
  // Quarters
  'الربع 1': 'Q1',
  'الربع 2': 'Q2',
  'الربع 3': 'Q3',
  'الربع 4': 'Q4',
  'الربع الأول': 'Q1',
  'الربع الثاني': 'Q2',
  'الربع الثالث': 'Q3',
  'الربع الرابع': 'Q4',
  'الربع الأول 2025': 'Q1 2025',
  'الربع الثاني 2025': 'Q2 2025',
  'الربع الثالث 2025': 'Q3 2025',
  'الربع الرابع 2025': 'Q4 2025',
  // Half-yearly
  'النصف الأول': 'H1',
  'النصف الثاني': 'H2',
  'النصف الأول 2025': 'H1 2025',
  'النصف الثاني 2025': 'H2 2025',
  // Yearly
  'سنة 2025': 'Year 2025',
  'سنة 2026': 'Year 2026',
  'سنة 2027': 'Year 2027',
  // Academic year
  'السنة الأكاديمية 2025-2026': 'Academic Year 2025-2026',
  'السنة الأكاديمية 2026-2027': 'Academic Year 2026-2027',
  'الفصل الأول 2025-2026': 'Semester 1 (2025-2026)',
  'الفصل الثاني 2025-2026': 'Semester 2 (2025-2026)',
  'الفصل الأول 2026-2027': 'Semester 1 (2026-2027)',
  'الفصل الثاني 2026-2027': 'Semester 2 (2026-2027)',
  // Monthly
  'شهر 1': 'Month 1',
  'شهر 2': 'Month 2',
  'شهر 3': 'Month 3',
  'شهر 4': 'Month 4',
  'شهر 5': 'Month 5',
  'شهر 6': 'Month 6',
  'شهر 7': 'Month 7',
  'شهر 8': 'Month 8',
  'شهر 9': 'Month 9',
  'شهر 10': 'Month 10',
  'شهر 11': 'Month 11',
  'شهر 12': 'Month 12',
  // Month names
  'يناير 2026': 'January 2026',
  'فبراير 2026': 'February 2026',
  'مارس 2026': 'March 2026',
  'أبريل 2026': 'April 2026',
  'مايو 2026': 'May 2026',
  'يونيو 2026': 'June 2026',
  'يوليو 2026': 'July 2026',
  'أغسطس 2026': 'August 2026',
  'سبتمبر 2025': 'September 2025',
  'أكتوبر 2025': 'October 2025',
  'نوفمبر 2025': 'November 2025',
  'ديسمبر 2025': 'December 2025',
  // Monthly with year format
  'شهر 1 - 2025': 'Month 1 - 2025',
  'شهر 2 - 2025': 'Month 2 - 2025',
  'شهر 3 - 2025': 'Month 3 - 2025',
  'شهر 4 - 2025': 'Month 4 - 2025',
  'شهر 5 - 2025': 'Month 5 - 2025',
  'شهر 6 - 2025': 'Month 6 - 2025',
  'شهر 7 - 2025': 'Month 7 - 2025',
  'شهر 8 - 2025': 'Month 8 - 2025',
  'شهر 9 - 2025': 'Month 9 - 2025',
  'شهر 10 - 2025': 'Month 10 - 2025',
  'شهر 11 - 2025': 'Month 11 - 2025',
  'شهر 12 - 2025': 'Month 12 - 2025',
};

// KPI-related phrases
const kpiTranslations: Record<string, string> = {
  'نسبة تحقيق الهدف': 'Goal Achievement Rate',
  'نسبة الإنجاز': 'Completion Rate',
  'معدل رضا المتعاملين': 'Customer Satisfaction Rate',
  'زمن الاستجابة': 'Response Time',
  'معدل الأخطاء': 'Error Rate',
  'نسبة الالتزام': 'Compliance Rate',
  'معدل الإنتاجية': 'Productivity Rate',
  'نسبة التوفير': 'Savings Rate',
  'معدل التحسين': 'Improvement Rate',
  'نسبة التغطية': 'Coverage Rate',
};

// =============================================================================
// BUILD COMPLETE TRANSLATION MAP
// =============================================================================

// Merge all translations into one comprehensive map (Arabic -> English)
const arabicToEnglish: Record<string, string> = {
  ...organizationTranslations,
  ...unitTypeTranslations,
  ...statusTranslations,
  ...commonTranslations,
  ...goalTranslations,
  ...processTranslations,
  ...kpiTranslations,
  ...periodTranslations,
};

// Create reverse map (English -> Arabic)
const englishToArabic: Record<string, string> = {};
Object.entries(arabicToEnglish).forEach(([arabic, english]) => {
  englishToArabic[english] = arabic;
  // Also add lowercase version for case-insensitive matching
  englishToArabic[english.toLowerCase()] = arabic;
});
// Additional English keys stored by unit goal screens
englishToArabic['currency'] = 'درهم';
englishToArabic['number'] = 'عدد';
englishToArabic['day'] = 'يوم';
englishToArabic['hour'] = 'ساعة';

// =============================================================================
// TRANSLATION FUNCTIONS
// =============================================================================

/**
 * Detects if text is Arabic
 */
const isArabicText = (text: string): boolean => {
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  return arabicPattern.test(text);
};

/**
 * Main translation function - translates any text based on current language
 * Works like Google Translate - automatically detects and translates
 *
 * @param text - The text to translate
 * @param targetLanguage - Target language ('ar' or 'en')
 * @returns Translated text or original if no translation found
 */
export const translateText = (
  text: string | undefined | null,
  targetLanguage: string
): string => {
  if (!text || typeof text !== 'string') return '';

  const trimmedText = text.trim();
  if (!trimmedText) return '';

  const isArabic = isArabicText(trimmedText);

  // If target is Arabic
  if (targetLanguage === 'ar') {
    // Text is already Arabic, return as is
    if (isArabic) return trimmedText;
    // Text is English, translate to Arabic
    return englishToArabic[trimmedText] || englishToArabic[trimmedText.toLowerCase()] || trimmedText;
  }

  // If target is English
  if (targetLanguage === 'en') {
    // Text is already English, return as is
    if (!isArabic) return trimmedText;
    // Text is Arabic, translate to English (exact match first)
    if (arabicToEnglish[trimmedText]) return arabicToEnglish[trimmedText];
    // Pattern-based translations for dynamic labels
    const weekMatch = trimmedText.match(/^أسبوع\s+(\d+)$/);
    if (weekMatch) return `Week ${weekMatch[1]}`;
    const dayMatch = trimmedText.match(/^يوم\s+(\d+)$/);
    if (dayMatch) return `Day ${dayMatch[1]}`;
    return trimmedText;
  }

  return trimmedText;
};

/**
 * Translates organization name
 */
export const translateOrganizationName = (
  name: string | undefined | null,
  language: string
): string => {
  return translateText(name, language);
};

/**
 * Translates unit type
 */
export const translateUnitType = (
  type: string | undefined | null,
  language: string
): string => {
  return translateText(type, language);
};

/**
 * Translates status label
 */
export const translateStatus = (
  status: string | undefined | null,
  language: string
): string => {
  return translateText(status, language);
};

/**
 * Generic dynamic content translator
 */
export const translateDynamicContent = (
  content: string | undefined | null,
  translations: Record<string, string>,
  language: string
): string => {
  if (!content) return '';

  if (language === 'ar') {
    // Check reverse translation
    const reverseMap: Record<string, string> = {};
    Object.entries(translations).forEach(([ar, en]) => {
      reverseMap[en] = ar;
    });
    return reverseMap[content] || content;
  }

  return translations[content] || content;
};

/**
 * Batch translate multiple texts
 */
export const translateBatch = (
  texts: (string | undefined | null)[],
  language: string
): string[] => {
  return texts.map(text => translateText(text, language));
};

/**
 * Add custom translations at runtime
 * Useful for adding translations from backend data
 */
export const addTranslations = (translations: Record<string, string>): void => {
  Object.entries(translations).forEach(([arabic, english]) => {
    arabicToEnglish[arabic] = english;
    englishToArabic[english] = arabic;
    englishToArabic[english.toLowerCase()] = arabic;
  });
};

/**
 * Get all available translations (for debugging)
 */
export const getAllTranslations = () => ({
  arabicToEnglish,
  englishToArabic,
  count: Object.keys(arabicToEnglish).length,
});

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  translateText,
  translateOrganizationName,
  translateUnitType,
  translateStatus,
  translateDynamicContent,
  translateBatch,
  addTranslations,
  getAllTranslations,
  isArabicText,
};
